import React, { useState } from 'react';
import { Modal } from '../shared';
import { useProfileMappings } from '../../hooks';
import styles from './ProfileMappingsManager.module.css';

interface ProfileMappingsManagerProps {
  isOpen: boolean;
  profileId: string;
  profileName: string;
  projectId: string;
  onClose: () => void;
}

export function ProfileMappingsManager({
  isOpen,
  profileId,
  profileName,
  projectId,
  onClose
}: ProfileMappingsManagerProps) {
  const {
    mappedApplications,
    availableApplications,
    addMapping,
    removeMapping
  } = useProfileMappings(profileId, projectId);

  const [isAdding, setIsAdding] = useState<string | null>(null);
  const [isRemoving, setIsRemoving] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleAddMapping = async (appId: string) => {
    setIsAdding(appId);
    setError(null);

    const result = await addMapping(appId);

    if (!result.success) {
      setError(result.error || 'Failed to add mapping');
    }

    setIsAdding(null);
  };

  const handleRemoveMapping = async (appId: string) => {
    setIsRemoving(appId);
    setError(null);

    const result = await removeMapping(appId);

    if (!result.success) {
      setError(result.error || 'Failed to remove mapping');
    }

    setIsRemoving(null);
  };

  return (
    <Modal
      isOpen={isOpen}
      title="Manage Applications"
      subtitle={<span>for <strong>{profileName}</strong></span>}
      onClose={onClose}
      size="large"
    >
      <div className={styles.container}>
        {error && (
          <div className={styles.error}>{error}</div>
        )}

        <div className={styles.section}>
          <h3 className={styles.sectionTitle}>
            Mapped Applications ({mappedApplications.length})
          </h3>
          {mappedApplications.length === 0 ? (
            <p className={styles.emptyText}>
              No applications mapped to this profile yet.
            </p>
          ) : (
            <div className={styles.list}>
              {mappedApplications.map(app => (
                <div key={app.id} className={styles.item}>
                  <div className={styles.itemInfo}>
                    <div className={styles.itemName}>{app.name}</div>
                  </div>
                  <button
                    onClick={() => handleRemoveMapping(app.id)}
                    disabled={isRemoving === app.id || isAdding !== null}
                    className={styles.buttonDanger}
                  >
                    {isRemoving === app.id ? 'Removing...' : 'Remove'}
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className={styles.section}>
          <h3 className={styles.sectionTitle}>
            Available Applications ({availableApplications.length})
          </h3>
          {availableApplications.length === 0 ? (
            <p className={styles.emptyText}>
              All applications in this project are already mapped.
            </p>
          ) : (
            <div className={styles.list}>
              {availableApplications.map(app => (
                <div key={app.id} className={styles.item}>
                  <div className={styles.itemInfo}>
                    <div className={styles.itemName}>{app.name}</div>
                  </div>
                  <button
                    onClick={() => handleAddMapping(app.id)}
                    disabled={isAdding === app.id || isAdding !== null || isRemoving !== null}
                    className={styles.buttonPrimary}
                  >
                    {isAdding === app.id ? 'Adding...' : 'Add'}
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className={styles.footer}>
          <button
            onClick={onClose}
            className={styles.buttonSecondary}
          >
            Close
          </button>
        </div>
      </div>
    </Modal>
  );
}
